import React, { useEffect } from "react";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

/**
 * Reusable media lightbox.
 * @param {boolean} open
 * @param {(open:boolean)=>void} onOpenChange
 * @param {Array<{src:string, caption?:string, subcaption?:string}>} items
 * @param {number} index
 * @param {(i:number)=>void} setIndex
 */
export const MediaLightbox = ({ open, onOpenChange, items, index, setIndex }) => {
  const total = items?.length || 0;

  const next = () => setIndex((index + 1) % total);
  const prev = () => setIndex((index - 1 + total) % total);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, total]);

  if (!total) return null;
  const current = items[index];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-black/95 border-white/10 text-white max-w-[95vw] md:max-w-5xl p-0 overflow-hidden [&>button]:hidden"
        data-testid="media-lightbox"
      >
        <div className="relative aspect-[4/5] sm:aspect-video bg-black">
          <img
            src={current.src}
            alt={current.caption || `Kép ${index + 1}`}
            className="absolute inset-0 h-full w-full object-contain"
          />
          {(current.caption || current.subcaption) && (
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
              {current.caption && (
                <p className="font-display text-xl md:text-2xl font-bold text-white">
                  {current.caption}
                </p>
              )}
              {current.subcaption && (
                <p className="font-body text-sm text-white/70 mt-1">
                  {current.subcaption}
                </p>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Bezárás"
            data-testid="media-lightbox-close"
            className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center bg-black/60 border border-white/20 text-white hover:bg-[#FF007F] hover:border-[#FF007F] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>

          {total > 1 && (
            <>
              <button
                type="button"
                onClick={prev}
                aria-label="Előző"
                data-testid="media-lightbox-prev"
                className="absolute left-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center bg-black/60 border border-white/20 text-white hover:bg-[#00E5FF] hover:text-black hover:border-[#00E5FF] transition-all"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={next}
                aria-label="Következő"
                data-testid="media-lightbox-next"
                className="absolute right-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center bg-black/60 border border-white/20 text-white hover:bg-[#00E5FF] hover:text-black hover:border-[#00E5FF] transition-all"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="absolute top-4 left-4 px-3 py-1.5 bg-black/60 border border-white/15 font-display text-xs tracking-[0.3em] text-white">
                {String(index + 1).padStart(2, "0")} /{" "}
                {String(total).padStart(2, "0")}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaLightbox;
